/**
 * Hub's voice room (visual-overhaul.md §6.8, §7.17): one tile per person on
 * a stage (name plate, state badges, the talk ring while transmitting), and
 * a control bar (the split mic with its input-mode menu, deafen, voice
 * settings, leave). The header has no connection chip: the dock's voice row
 * says "Voice connected" and the ping (L10, L25); the bitrate is the
 * channel name's tooltip. Its buttons, as Discord's: Show non-video
 * participants (while anything has video), full screen for the whole room,
 * and the chat with its unread mark. Alone in the room, a card says so and
 * offers Copy invite.
 *
 * Phones and tablets (§12.7 B): the room is the main pane; the tiles are one
 * column on a phone in portrait and two on its side or on a tablet; a tap on
 * a tile (as a long-press) opens the member menu, an action sheet; push to
 * talk has a hold button; the room's chat is a pane on the back stack.
 */
import { Permissions, type Pid, type Snowflake } from '@neosocial/protocol';
import { pushBack } from '@neosocial/ui/back';
import { readLayout, useLayout, type LayoutState } from '@neosocial/ui/layout';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { tr, useI18n, useT } from '../i18n';
import { asSentence } from '../i18n/core';
import { api } from '../lib/api';
import { copyText } from '../lib/clipboard';
import { serverOrigin } from '../lib/config';
import { runAction } from '../mod/actions';
import { useData } from '../store/data';
import { useDataShallow } from '../store/data';
import { can, channelPermissions, displayName, unreadOf, voiceStatesIn } from '../store/selectors';
import { sortByVoiceOrder } from '../store/voiceOrder';
import { goHub } from '../store/ui';
import { usePanes } from '../shell/panes';
import {
  leaveVoice,
  pttHold,
  pttMode,
  setLocalMute,
  setUserVolume,
  toggleDeaf,
  toggleMute,
} from '../voice/controller';
// the member menus' Mention, Message, Add note and Copy User ID; the quick device menus
import { isDeletedUser } from '../store/selectors';
import { useUi } from '../store/ui';
import { noteMenuItems } from '../social/ProfileSocial';
import { insertIntoDraft } from './Composer';
import { openDm } from './social';
import { openInputMenu, openOutputMenu } from './voiceQuick';
import { MAX_USER_VOLUME } from '../voice/types';
import {
  inMyVoiceChannel,
  myServerMute,
  myVoiceBlocked,
  rosterPeople,
  userSpeaking,
  type VoiceSession,
} from '../voice/machine';
import {
  channelStatusKey,
  micToggleView,
  ownBlockLine,
  serverStatusKey,
  soundToggleView,
  type SelfBlock,
} from '../voice/flags';
import {
  SelfToggleButton,
  useOwnServerFlags,
  useSelfLift,
  useVoiceFlags,
  VoiceFlagIcons,
} from '../voice/VoiceFlags';
import { openMemberMenu, type LiveExtra } from '../mod/memberMenu';
import { openProfile } from './ProfileCard';
import { keyLabel } from '../voice/ptt';
import { PriorityBadge, RoomStatus } from '../voice/VoiceExtras';
import { useVoice, useVoiceQuiet } from '../voice/store';
import { Avatar, Btn, gradStyle, Icon, IconBtn } from '../ui/kit';
import { rememberAnchor, toast, type Anchor, type MenuItem } from '../ui/overlay';
import { useLocalMedia, watchStream } from '../voice/live';
import { openSettings } from '../store/ui';
import { ChannelView } from './ChannelView';
import { joinVoiceFromUi } from './voiceUi';
import { joinAndWatch } from './streamUi';
import {
  CameraButton,
  cameraPidOf,
  fullscreenElement,
  fullscreenSupported,
  roomStreams,
  sameFocus,
  ShareButton,
  StreamTile,
  toggleFullscreen,
  useCameraStream,
  useFullscreen,
  useVideoVisibility,
  VideoView,
  type FocusKey,
  type TileTracker,
} from './Streams';
import {
  keepFocus,
  setStripHidden,
  setVideoOff,
  setVideoOnly,
  useStreamView,
  type TileTap,
} from './streamView';
import { useMediaOf } from '../voice/VoiceFlags';
import type { RosterMember } from '../voice/types';
import type { VoiceVideoRef } from '@neosocial/protocol';

/** A tile's person; their flags come from `useVoiceFlags` (roster and gateway, merged). */
interface Person {
  userId: Snowflake;
}

/**
 * Who is in the room: the live roster while connected there, else voice
 * states. Either way in the sidebar's order: highest role, then name
 * (store/voiceOrder.ts), so the tiles and the sidebar rows agree.
 */
export function useRoomPeople(channelId: Snowflake): Person[] {
  const s = useVoiceQuiet();
  // a hidden room you are in has no channel here: the session's server
  const gid =
    useData((d) => d.channels[channelId]?.guild_id ?? null) ??
    (s.channelId === channelId ? s.guildId : null);
  const lang = useI18n((x) => x.lang);
  const states = useDataShallow((d) => voiceStatesIn(d, gid, channelId, lang));
  const meId = useData((d) => d.me?.id);
  const guild = useData((d) => (gid ? d.guilds[gid] : undefined));
  const members = useData((d) => (gid ? d.members[gid] : undefined));
  const users = useData((d) => d.users);
  if ((s.status === 'connected' || s.status === 'connecting') && s.pid !== null) {
    const out: Person[] = [];
    // one tile per person; yours only from your own pid (live bug 2026-10-28)
    for (const m of rosterPeople(s, meId, channelId)) out.push({ userId: m.user_id });
    // the roster includes you once the engine has welcomed you
    if (s.channelId === channelId && meId && !out.some((p) => p.userId === meId))
      out.push({ userId: meId });
    return sortByVoiceOrder(out, (p) => p.userId, { guild, members, users, lang });
  }
  return states.map((v) => ({ userId: v.user_id }));
}

/**
 * A person's own tile (their avatar or camera) in the voice room on screen
 * now (a new one after they rejoin), if any; never their screen's tile,
 * which comes first on the stage (V-02: the camera menu's Full screen took it).
 */
function tileOf(userId: Snowflake): HTMLElement | null {
  return document.querySelector<HTMLElement>(`.hubv-tile[data-kind="av"][data-user="${userId}"]`);
}

/**
 * Where a tile's menu was opened: the tile, and the element its pop-ups
 * open on (the More button, or the tile for a right-click). Both are
 * remembered (`rememberAnchor`), so a pop-up still opens there after the
 * tile has left the page.
 */
interface TileOpener {
  tile: HTMLElement;
  anchor: HTMLElement;
}

/**
 * A tile's own items in its member menu: View profile, and while the person
 * is in your voice channel their User volume (a slider item, 0-200%, P12)
 * and "Mute for me"; on your own tile, Deafen and Input settings. Live (QA
 * 2026-10-28 round 3): an open menu drops the voice items when the person
 * leaves voice or moves away (the tile unmounts), brings them back if they
 * return, and the slider and "Mute for me" follow changes made elsewhere.
 *
 * QA 2026-10-28 round 4: View profile opened in the top-left corner once
 * the tile the menu was opened on had left the page (its rect reads all
 * zeros). It opens beside the person's tile as it is now (a new one after a
 * rejoin), else where the menu was opened.
 */
function tileMenuExtra(
  userId: Snowflake,
  meId: Snowflake | undefined,
  from: TileOpener,
): LiveExtra {
  const tileNow = () => tileOf(userId) ?? from.tile;
  if (userId === meId) return ownTileExtra(userId, tileNow);
  return voiceMenuExtra(userId, meId, tileNow);
}

/** A person's row under a voice channel in the sidebar now (a new one after they rejoin), if any. */
function rowOf(userId: Snowflake): HTMLElement | null {
  return document.querySelector<HTMLElement>(`.vc-u[data-user="${userId}"]`);
}

/**
 * The sidebar voice row's own items, the same as a tile's (Discord offers
 * User Volume and Mute on the sidebar's voice entry too; QA 2026-10-28: the
 * row's menu had neither): View profile beside the row, and while the
 * person is in your voice channel their volume slider and "Mute for me", live.
 */
export function rowMenuExtra(
  userId: Snowflake,
  meId: Snowflake | undefined,
  row: HTMLElement,
): LiveExtra {
  return voiceMenuExtra(userId, meId, () => rowOf(userId) ?? row);
}

/** A per-person volume in percent, as the slider and its value say it. */
const percent = (v: number) =>
  tr().fmt.num(v / 100, { style: 'percent', maximumFractionDigits: 0 });

/**
 * View profile (opening beside `profileAt()`), and while the person is in
 * your voice channel their User volume (a slider item from 0 to 200%, which
 * never closes the menu) and "Mute for me".
 */
/**
 * The channel a person is live in now (the live roster, else the gateway's
 * voice state), or null: their row's menu offers Watch stream there.
 */
function liveChannelOf(userId: Snowflake, meId: Snowflake | undefined): Snowflake | null {
  if (userId === meId) return null;
  const s = useVoice.getState().session;
  for (const m of Object.values(s.members) as RosterMember[])
    if (m.user_id === userId && m.screen) return m.channel;
  const d = useData.getState();
  for (const g of Object.values(d.voiceStates)) {
    const vs = g?.[userId];
    if (vs?.channel_id && vs.self_stream) return vs.channel_id;
  }
  return null;
}

/** The voice channel a person is in now (the live roster, else the gateway's voice state), or null. */
function voiceChannelOf(userId: Snowflake): Snowflake | null {
  const s = useVoice.getState().session;
  for (const m of Object.values(s.members) as RosterMember[])
    if (m.user_id === userId) return m.channel;
  for (const g of Object.values(useData.getState().voiceStates)) {
    const vs = g?.[userId];
    if (vs?.channel_id) return vs.channel_id;
  }
  return null;
}

/**
 * Where a voice member menu's Mention writes `<@id>` (Discord's): the text
 * channel on screen when it is in the person's server, else the chat of the
 * room they are in, which opens (a DM call's DM); null when neither can
 * take it (a hidden room).
 */
function mentionIn(userId: Snowflake): (() => void) | null {
  const d = useData.getState();
  const hub = useUi.getState().hub;
  const roomId = voiceChannelOf(userId);
  const room = roomId ? d.channels[roomId] : undefined;
  const text = `<@${userId}>`;
  const onScreen = hub?.kind === 'channel' ? d.channels[hub.channelId] : undefined;
  if (onScreen && room?.guild_id && onScreen.guild_id === room.guild_id)
    return () => insertIntoDraft(onScreen.id, text);
  if (!room) return null;
  return () => {
    goHub(
      room.guild_id
        ? { kind: 'voice', channelId: room.id, chat: true }
        : { kind: 'home', channelId: room.id },
    );
    insertIntoDraft(room.id, text);
  };
}

/** Copy User ID (Discord's): the ID on the clipboard, or the ID to copy by hand. */
function copyUserId(userId: Snowflake): void {
  const { t } = tr();
  void copyText(userId).then((ok) =>
    ok
      ? toast(t('c.copied'), { icon: 'check' })
      : toast(t('ns.copy.failed', { text: userId }), {
          icon: 'alert',
          tone: 'warn',
          duration: 9000,
        }),
  );
}

/** Mention (when somewhere can take it), the voice member menus' item after View profile. */
function mentionItems(userId: Snowflake): MenuItem[] {
  if (!mentionIn(userId)) return [];
  return [
    {
      id: 'mention',
      label: tr().t('vx.m.mention'),
      icon: 'at',
      run: () => mentionIn(userId)?.(),
    },
  ];
}

/** The voice member menus' last item, after the moderator's (Discord's place for it). */
const copyIdTail = (userId: Snowflake) => (): MenuItem[] => [
  { id: 'copy-id', label: tr().t('vx.m.copyId'), icon: 'copy', run: () => copyUserId(userId) },
];

function voiceMenuExtra(
  userId: Snowflake,
  meId: Snowflake | undefined,
  profileAt: () => HTMLElement,
): LiveExtra {
  const here = () => inMyVoiceChannel(useVoice.getState().session, userId, meId);
  const lmuted = () => !!useVoice.getState().session.localMutes[userId];
  const volume = () => Math.round((useVoice.getState().session.volumes[userId] ?? 1) * 100);
  const liveIn = () => liveChannelOf(userId, meId);
  const watching = () => {
    const s = useVoice.getState().session;
    return Object.values(s.members).some((m) => m.user_id === userId && !!s.watching[m.pid]);
  };
  return {
    key: () => JSON.stringify([here(), lmuted(), here() ? volume() : null, liveIn(), watching()]),
    subscribe: (onChange) => useVoice.subscribe(onChange),
    build: () => {
      const { t } = tr();
      const items: MenuItem[] = [
        { label: t('pres.profile'), icon: 'user', run: () => openProfile(userId, profileAt()) },
        ...mentionItems(userId),
      ];
      // Message and Add note (Discord's), never on yourself; the profile
      // card's own More menu has its Message button, so not memberMenuItems
      const u = useData.getState().users[userId];
      if (userId !== meId && u && !isDeletedUser(u))
        items.push({
          id: 'message',
          label: t('hub.prof.message'),
          icon: 'chats',
          run: () => void openDm(userId),
        });
      items.push(...noteMenuItems(userId, () => openProfile(userId, profileAt())));
      // Discord's "Watch Stream" on a live person (from elsewhere: join, then watch)
      const ch = liveIn();
      if (ch && !watching())
        items.push({
          id: 'watch-stream',
          label: t(here() ? 'stream.watch' : 'stream.watchJoin'),
          icon: 'eye',
          run: () => void joinAndWatch(ch, userId),
        });
      if (!here()) return items;
      const muted = lmuted();
      items.push(
        {
          id: 'volume',
          label: t('ns.voice.volume'),
          range: {
            value: volume(),
            min: 0,
            max: MAX_USER_VOLUME * 100,
            step: 5,
            format: percent,
            onChange: (v) => setUserVolume(userId, v / 100),
          },
        },
        {
          id: 'local-mute',
          label: t(muted ? 'ns.voice.unmuteLocal' : 'ns.voice.muteLocal'),
          icon: muted ? 'volume' : 'volume-x',
          run: () => setLocalMute(userId, !muted),
        },
      );
      return items;
    },
    tail: copyIdTail(userId),
  };
}

/**
 * Your own tile's items (P12): View profile and Mention, and while you are
 * connected Mute and Deafen (or their undo) and Input settings, as the bar
 * offers them; Copy User ID last.
 */
function ownTileExtra(userId: Snowflake, profileAt: () => HTMLElement): LiveExtra {
  const on = () => useVoice.getState().session.status === 'connected';
  const deaf = () => useVoice.getState().session.selfDeaf;
  // a click on the mic while deafened undeafens and unmutes (toggleMute): "Unmute"
  const muted = () => useVoice.getState().session.selfMute || deaf();
  return {
    key: () => JSON.stringify([on(), deaf(), muted()]),
    subscribe: (onChange) => useVoice.subscribe(onChange),
    build: () => {
      const { t } = tr();
      const items: MenuItem[] = [
        { label: t('pres.profile'), icon: 'user', run: () => openProfile(userId, profileAt()) },
        ...mentionItems(userId),
      ];
      if (!on()) return items;
      items.push(
        {
          id: 'self-mute',
          label: t(muted() ? 'dock.unmute' : 'dock.mute'),
          icon: muted() ? 'mic' : 'mic-off',
          run: toggleMute,
        },
        {
          id: 'self-deaf',
          label: t(deaf() ? 'dock.undeafen' : 'dock.deafen'),
          icon: deaf() ? 'headphones' : 'headphones-off',
          run: toggleDeaf,
        },
        {
          id: 'input-settings',
          label: t('hubv.vr.inputSettings'),
          icon: 'sliders',
          run: () => openSettings('voice'),
        },
      );
      return items;
    },
    tail: copyIdTail(userId),
  };
}

/**
 * The avatar on a tile: 80 (§6.8) while it clears the name plate (8 + 24
 * under it, centred: a tile 144 tall), else 40, and 32 on the smallest.
 */
function tileAvatar(tileWidth: number): number {
  const h = (tileWidth * 9) / 16;
  return h >= 144 ? 80 : h >= 96 ? 40 : 32;
}

/**
 * A camera tile's own items, after the rest (Discord's): Full screen while
 * the camera is on, and on someone else's camera Disable video (or Enable
 * video again), which only this client knows: none of that camera comes
 * while it is off, and the tile shows the avatar.
 */
function cameraMenuExtra(
  base: LiveExtra,
  userId: Snowflake,
  me: boolean,
  channelId: Snowflake,
  tileNow: () => HTMLElement,
): LiveExtra {
  const camera = () =>
    !me && cameraPidOf(useVoice.getState().session.members, userId, channelId) !== null;
  const off = () => !!useStreamView.getState().videoOff[userId];
  const showing = () => (me ? !!useLocalMedia.getState().camera : camera() && !off());
  return {
    key: () => base.key() + '\n' + JSON.stringify([camera(), off(), showing()]),
    subscribe: (onChange) => {
      const offs = [
        base.subscribe(onChange),
        useStreamView.subscribe(onChange),
        useLocalMedia.subscribe(onChange),
      ];
      return () => offs.forEach((f) => f());
    },
    build: () => {
      const { t } = tr();
      const own: MenuItem[] = [];
      if (showing())
        own.push({
          id: 'fullscreen',
          label: t(
            fullscreenElement() === tileNow() ? 'stream.exitFullscreen' : 'stream.fullscreen',
          ),
          icon: 'win-max',
          run: () => toggleFullscreen(tileNow()),
        });
      if (camera())
        own.push({
          id: 'video-off',
          label: t(off() ? 'hubv.vr.videoOn' : 'hubv.vr.videoOff'),
          icon: off() ? 'video' : 'video-off',
          run: () => setVideoOff(userId, !off()),
        });
      const items = base.build();
      return own.length ? [...items, { sep: true }, ...own] : items;
    },
    // the base's last items (Copy User ID) stay last
    tail: () => base.tail?.() ?? [],
  };
}

function Tile({
  p,
  meId,
  s,
  i,
  av,
  guildId,
  channelId,
  track,
  focused,
  onTap,
}: {
  p: Person;
  meId?: Snowflake;
  s: VoiceSession;
  i: number;
  av: number;
  guildId: Snowflake | null;
  channelId: Snowflake;
  track: TileTracker;
  focused: boolean;
  /** A click on a camera: focus it (the stage's; a double-click goes full screen instead). */
  onTap: (how?: TileTap) => void;
}) {
  const { t } = useT();
  const user = useData((d) => d.users[p.userId]);
  const name = useData((d) =>
    user ? displayName(user, guildId ? d.members[guildId]?.[p.userId] : undefined) : p.userId,
  );
  // its own selector: the room's session (`useVoiceQuiet`) skips ring changes
  const speaking = useVoice(
    (v) => v.session.status === 'connected' && userSpeaking(v.session, p.userId, meId),
  );
  const me = p.userId === meId;
  const lmuted = !!s.localMutes[p.userId];
  // a camera replaces the avatar (§4.7): yours as a local preview, others'
  // while on screen, unless you turned theirs off for yourself (Disable video)
  const videoOff = useStreamView((v) => !me && !!v.videoOff[p.userId]);
  const livePid = me ? null : cameraPidOf(s.members, p.userId, channelId);
  const stream = useCameraStream(p.userId, me);
  const cam = videoOff ? null : stream;
  const camPid = videoOff ? null : livePid;
  const ref = useRef<HTMLDivElement | null>(null);
  const full = useFullscreen(ref);
  const tracked = camPid !== null;
  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      ref.current = el;
      const off = tracked ? track(el) : undefined;
      return () => {
        ref.current = null;
        off?.();
      };
    },
    [track, tracked],
  );
  // every state at once, a moderator's apart (live bug 2026-10-28); your own
  // tile shows your own mute and deafen too (P20)
  const flags = useVoiceFlags(p.userId, guildId);
  /**
   * The same member menu as everywhere, with this tile's own items. From
   * the More button it opens on the button, which `place` keeps where it
   * was if the tile leaves the page while the menu is open (QA 2026-10-28
   * round 4); from a right-click at the pointer; from the keyboard
   * (Shift+F10, the menu key) on the tile.
   */
  const menu = (anchor: HTMLElement, at?: Anchor) => {
    const tile = anchor.closest<HTMLElement>('.hubv-tile') ?? anchor;
    rememberAnchor(tile);
    rememberAnchor(anchor);
    openMemberMenu(
      at ?? anchor,
      guildId,
      p.userId,
      cameraMenuExtra(
        tileMenuExtra(p.userId, meId, { tile, anchor }),
        p.userId,
        me,
        channelId,
        () => tileOf(p.userId) ?? tile,
      ),
    );
  };
  return (
    <div
      ref={setRef}
      className={
        'hubv-tile' +
        (me ? ' me' : '') +
        (lmuted ? ' lmuted' : '') +
        (speaking ? ' is-speaking' : '') +
        (cam ? ' has-video' : '') +
        (focused ? ' focused' : '')
      }
      data-kind="av"
      data-user={p.userId}
      data-vref={camPid !== null ? `${camPid}:camera` : undefined}
      tabIndex={0}
      role="group"
      style={{ ...gradStyle(p.userId), '--i': i } as CSSProperties}
      aria-label={name + flags.map((f) => ', ' + t(f.label)).join('')}
      onClick={(e) => {
        if ((e.target as Element).closest('button')) return;
        // a camera focuses, by a click or a tap (Discord's video tile, QA
        // CAM-1), and a double-click goes full screen (its second click is
        // the stage's, which undoes the focus first: QA SS-3); a finger has
        // no hover (no More button), so a tap on an avatar opens the member
        // menu, as a long-press does on either (Discord's mobile tile); a
        // mouse click does nothing on an avatar
        if (cam) {
          // a pointer's first click waits for a second (detail 0: the keyboard's, at once)
          if (e.detail < 2) onTap({ wait: e.detail === 1 });
          return;
        }
        if (readLayout().pointer === 'coarse') menu(e.currentTarget);
      }}
      // Enter or Space on the focused tile: a camera focuses, as its click;
      // an avatar opens the member menu, as a tap does
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        if (e.repeat) return;
        if (cam) onTap({ key: true });
        else menu(e.currentTarget);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        // Shift+F10 and the menu key give no pointer: the menu opens on the tile
        const tile = e.currentTarget;
        menu(tile, e.clientX || e.clientY ? { x: e.clientX, y: e.clientY } : tile);
      }}
    >
      <span className="hubv-tile-av">
        {cam ? (
          <VideoView stream={cam} fit="cover" mirror={me} />
        ) : (
          <Avatar user={user} name={name} size={av} />
        )}
      </span>
      {videoOff && livePid !== null ? (
        <span className="hubv-tile-voff" role="img" aria-label={t('hubv.vr.videoIsOff')}>
          <Icon name="video-off" size="s" />
        </span>
      ) : null}
      {/* every tile, yours too (its menu: Mute, Deafen, Input settings) */}
      <span className="hubv-tile-tools">
        {/* full screen keeps its way out while the video reloads (QA FS-1) */}
        {cam || full ? (
          <button
            type="button"
            className="hubv-tt"
            aria-label={t(full ? 'stream.exitFullscreen' : 'stream.fullscreen')}
            data-tip={t(full ? 'stream.exitFullscreen' : 'stream.fullscreen')}
            data-tile-full=""
            onClick={(e) => {
              const tile = e.currentTarget.closest<HTMLElement>('.hubv-tile');
              if (tile) toggleFullscreen(tile);
            }}
          >
            <Icon name="win-max" size="m" />
          </button>
        ) : null}
        <button
          type="button"
          className="hubv-tt"
          aria-haspopup="menu"
          aria-label={t('hubv.vr.more')}
          data-tip={t('hubv.vr.more')}
          onClick={(e) => menu(e.currentTarget)}
        >
          <Icon name="more" size="m" />
        </button>
      </span>
      <span className="hubv-tile-foot">
        <span className="hubv-tile-name">
          <span dir="auto">{name}</span>
          <PriorityBadge userId={p.userId} cls="hubv-prio" />
        </span>
        {flags.length ? (
          <span className="hubv-tile-st">
            <VoiceFlagIcons flags={flags} cls="hubv-tf" />
          </span>
        ) : null}
      </span>
    </div>
  );
}

/** The stage's grid gap and the smallest tile it draws (§6.8). */
const GAP = 8;
const MIN_TILE = 160;

/**
 * The grid that makes the tiles as large as the stage allows (16:9 each),
 * in whole pixels (T3): the width a multiple of 16, so the height is whole
 * and a short last row centres on a whole pixel; `oy` centres the grid
 * vertically. Never below `MIN_TILE` (the stage scrolls then). `cols`: a
 * column count the layout sets (phones and tablets, `stageColumns`), never
 * more than there are people.
 */
export function bestFit(W: number, H: number, n: number, cols?: number) {
  const fixed = cols ? Math.max(1, Math.min(cols, n || 1)) : 0;
  let best = { cols: 1, rows: n, w: 0 };
  if (!n || W <= 0 || H <= 0) {
    const c = fixed || Math.min(n, 3) || 1;
    return { cols: c, rows: Math.ceil(n / c) || 1, w: 0, h: 0, oy: 0 };
  }
  for (let c = fixed || 1; c <= (fixed || n); c++) {
    const r = Math.ceil(n / c);
    const w = Math.min((W - (c - 1) * GAP) / c, ((H - (r - 1) * GAP) / r) * (16 / 9));
    if (w > best.w + 0.5) best = { cols: c, rows: r, w };
  }
  const w = Math.max(MIN_TILE, Math.floor(best.w / 16) * 16);
  const h = (w * 9) / 16;
  const gridH = best.rows * h + (best.rows - 1) * GAP;
  return { ...best, w, h, oy: Math.max(0, Math.floor((H - gridH) / 2)) };
}

/**
 * The stage's columns off the desktop (§12.7 B1): one on a phone in
 * portrait, two on a phone on its side and on a tablet. The desktop picks
 * freely (undefined), as before.
 */
export function stageColumns(l: LayoutState): number | undefined {
  if (l.layout === 'phone') return l.width > l.height ? 2 : 1;
  return l.layout === 'tablet' ? 2 : undefined;
}

/** What the stage shows: live screens first (Discord's order), then the people. */
type StageItem =
  | { kind: 'stream'; key: string; st: ReturnType<typeof roomStreams>[number] }
  | { kind: 'person'; key: string; p: Person };

/**
 * A stream tile under this height has no room for Watch between LIVE and
 * the name plate (64 of plates and margins, 36 of button): it is compact,
 * a tap or a click focuses it (QA PH-1, SS-4).
 */
const COMPACT_TILE_H = 100;

/** How long after a tile's click its double-click may still come (the OS decides; this bounds it). */
const DOUBLE_CLICK_MS = 800;

/**
 * How long a pointer's click on a video tile waits for a second click
 * before the focus moves: a double-click then goes straight to full screen,
 * with no focus view in between (QA V-06). A key focuses at once.
 */
export const TAP_WAIT_MS = 300;

/** How long ago `at` was (an event handler's clock, never a render's). */
const msSince = (at: number): number => Date.now() - at;

/** The stage's tile for `key` (a screen's, or a person's own), if it is on the stage. */
function stageTile(root: HTMLElement | null, key: FocusKey): HTMLElement | undefined {
  const kind = key.kind === 'stream' ? 'stream' : 'av';
  return [...(root?.querySelectorAll<HTMLElement>(`.hubv-tile[data-kind="${kind}"]`) ?? [])].find(
    (el) => el.dataset.user === key.userId,
  );
}

/**
 * Who on a stage has video: by user, the pid whose camera their tile shows
 * (null for your own, a local preview), leaving out a camera you turned off
 * for yourself (Disable video); and whether anything there has video, a live
 * screen included (the header's "Show non-video participants" then shows).
 */
export function useStageVideo(
  people: Person[],
  s: VoiceSession,
  channelId: Snowflake,
  meId: Snowflake | undefined,
): { cams: Map<Snowflake, Pid | null>; any: boolean } {
  const videoOff = useStreamView((v) => v.videoOff);
  const myCam = useLocalMedia((m) => !!m.camera);
  const cams = new Map<Snowflake, Pid | null>();
  for (const p of people) {
    if (p.userId === meId) {
      if (myCam) cams.set(p.userId, null);
      continue;
    }
    if (videoOff[p.userId]) continue;
    const pid = cameraPidOf(s.members, p.userId, channelId);
    if (pid !== null) cams.set(p.userId, pid);
  }
  const any =
    cams.size > 0 || Object.values(s.members).some((m) => m.channel === channelId && !!m.screen);
  return { cams, any };
}

/**
 * The tiles and live screens of a room (also a DM call's band, hub/DmCall.tsx).
 * Per room, as the focus (streamView): "Show non-video participants" off
 * leaves the people without video off the stage while anything has video,
 * and the focus view's strip folds away under its chevron.
 */
export function Stage({
  people,
  s,
  meId,
  guildId,
  channelId,
  chatOver,
  solo,
}: {
  people: Person[];
  s: VoiceSession;
  meId?: Snowflake;
  guildId: Snowflake | null;
  channelId: Snowflake;
  /** The room's chat covers the whole stage (a phone). */
  chatOver: boolean;
  /** A card after the tiles on the grid, a tile's size (the room's "It's just you here"). */
  solo?: ReactNode;
}) {
  const { t } = useT();
  const ref = useRef<HTMLDivElement>(null);
  const stripId = useId();
  const [fit, setFit] = useState({ cols: 3, rows: 1, w: 0, h: 0, oy: 0 });
  // the focus view (§4.7): one tile big, the others in a strip; kept while
  // the room is away, so coming back (the pop-out's "back" included) shows
  // what you left, and a stream you stop watching elsewhere (the pop-out)
  // lets it go (owner 2026-11-01)
  const kept = useStreamView((v) => v.focus[channelId] ?? null);
  const [focus, setFocus] = useState<FocusKey | null>(kept);
  const [seenKept, setSeenKept] = useState(kept);
  if (kept !== seenKept) {
    setSeenKept(kept);
    setFocus(kept);
  }
  useEffect(() => keepFocus(channelId, focus), [channelId, focus]);
  const videoOnly = useStreamView((v) => !!v.videoOnly[channelId]);
  const stripHidden = useStreamView((v) => !!v.stripHidden[channelId]);
  const video = useStageVideo(people, s, channelId, meId);
  /**
   * A video tile's last click, and the focus before it: a click focuses
   * once `TAP_WAIT_MS` passed with no second click, and the second click of
   * a double-click goes full screen instead, the layout untouched (§4.7, QA
   * V-06). A slow second click, after the focus moved, undoes it first; the
   * stage re-laid out between the two, so that click lands wherever (the
   * grid, another tile): the stage takes it (QA SS-3). A screen's tile or a
   * camera's, alike.
   */
  const lastTap = useRef<{ key: FocusKey; before: FocusKey | null; at: number } | null>(null);
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Drops a first click still waiting; true when there was one. */
  const cancelTap = () => {
    if (tapTimer.current === null) return false;
    clearTimeout(tapTimer.current);
    tapTimer.current = null;
    return true;
  };
  useEffect(() => {
    const timer = tapTimer;
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);
  // the tile Enter or Space focused: it keeps the keyboard focus where it moved to (V-09)
  const keyTap = useRef<FocusKey | null>(null);
  useLayoutEffect(() => {
    const k = keyTap.current;
    if (!k) return;
    keyTap.current = null;
    const tile = stageTile(ref.current, k);
    if (tile && document.activeElement !== tile) tile.focus({ preventScroll: true });
  }, [focus]);
  const onStageClick = (e: ReactMouseEvent) => {
    const last = lastTap.current;
    if (e.detail < 2 || !last || msSince(last.at) > DOUBLE_CLICK_MS) return;
    lastTap.current = null;
    // the first click still waits: nothing moved. Else back where it found
    // it (synchronously: the tile must be where it stays before it goes
    // full screen, or a re-mount ends that)
    if (!cancelTap()) flushSync(() => setFocus(last.before));
    const tile = stageTile(ref.current, last.key);
    if (tile?.classList.contains('has-video')) toggleFullscreen(tile);
  };
  const streams = roomStreams(s.members, channelId, s.pid);
  const shownPeople =
    videoOnly && video.any ? people.filter((p) => video.cams.has(p.userId)) : people;
  const items: StageItem[] = [
    ...streams.map((st) => ({ kind: 'stream' as const, key: `s:${st.userId}`, st })),
    ...shownPeople.map((p) => ({ kind: 'person' as const, key: p.userId, p })),
  ];
  const keyOf = (it: StageItem): FocusKey =>
    it.kind === 'stream'
      ? { kind: 'stream', userId: it.st.userId }
      : { kind: 'person', userId: it.p.userId };
  // a focused stream that ended, or a person who left, drops the focus view;
  // so does a focused camera that went off (Disable video: QA V-17)
  const focused = focus
    ? items.find(
        (it) =>
          sameFocus(keyOf(it), focus) && (it.kind === 'stream' || video.cams.has(it.p.userId)),
      )
    : undefined;
  const others = focused ? items.filter((it) => it !== focused) : [];
  const stripShown = others.length > 0 && !stripHidden;
  // the videos the stage shows, for `visible` (never your own: that is a
  // local preview); a folded strip's are not on screen
  const camRef = (it: StageItem): VoiceVideoRef | null => {
    if (it.kind === 'stream') return it.st.me ? null : { pid: it.st.pid, source: 'screen' };
    const pid = video.cams.get(it.p.userId);
    return pid === undefined || pid === null ? null : { pid, source: 'camera' };
  };
  const refs: VoiceVideoRef[] = [];
  for (const it of focused && !stripShown ? [focused] : items) {
    const r = camRef(it);
    if (r) refs.push(r);
  }
  const focusRef = focused ? camRef(focused) : null;
  // a layout change measures again; a phone turned on its side keeps its layout,
  // and its stage's resize measures again (the window's size is read then)
  const layout = useLayout().layout;
  // a phone shows one pane: the room is off screen behind the list, or
  // under its own chat; nothing is received then (§2.1 rule 3, QA TR-1)
  const listOver = usePanes((p) => p.pane === 'home');
  const roomShown = layout !== 'phone' || (!listOver && !chatOver);
  const track = useVideoVisibility(ref, refs, focusRef, roomShown, channelId);
  const n = items.length + (solo ? 1 : 0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const cs = getComputedStyle(el);
      const W = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const H = el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      const f = bestFit(W, H, n, stageColumns(readLayout()));
      setFit((cur) =>
        cur.cols === f.cols && cur.rows === f.rows && cur.w === f.w && cur.oy === f.oy ? cur : f,
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [n, layout]);
  const av = tileAvatar(fit.w || 480);
  // not measured yet (or no layout, as in tests): the stylesheet's 240x135
  const size: Record<string, string> = fit.w
    ? { '--tile-w': `${fit.w}px`, '--tile-h': `${fit.h}px` }
    : {};
  const tile = (it: StageItem, i: number, a: number, compact = false) => {
    const k = keyOf(it);
    const isFocused = it === focused;
    const onFocus = (on: boolean) => setFocus(on ? k : null);
    const go = () => {
      onFocus(!isFocused);
      // a compact stream you don't watch (the strip's) has no Watch: the
      // click that focuses it watches it too, as Discord's (QA V-13)
      if (compact && !isFocused && it.kind === 'stream' && !it.st.me && !s.watching[it.st.pid])
        void watchStream(it.st.pid, true);
    };
    const onTap = (how: TileTap = {}) => {
      lastTap.current = { key: k, before: focus, at: Date.now() };
      cancelTap();
      if (how.key) keyTap.current = k;
      if (!how.wait) return go();
      tapTimer.current = setTimeout(() => {
        tapTimer.current = null;
        go();
      }, TAP_WAIT_MS);
    };
    return it.kind === 'stream' ? (
      <StreamTile
        key={it.key}
        st={it.st}
        guildId={guildId}
        av={a}
        compact={compact}
        focused={isFocused}
        onFocus={onFocus}
        onTap={onTap}
        track={track}
        i={i}
      />
    ) : (
      <Tile
        key={it.key}
        p={it.p}
        s={s}
        meId={meId}
        i={i}
        av={a}
        guildId={guildId}
        channelId={channelId}
        track={track}
        focused={isFocused}
        onTap={onTap}
      />
    );
  };
  if (focused) {
    const fold = t(stripHidden ? 'hubv.vr.stripShow' : 'hubv.vr.stripHide');
    return (
      <div
        className="hubv-vr-stage"
        data-layout="focus"
        data-strip={others.length ? (stripShown ? 'shown' : 'hidden') : undefined}
        ref={ref}
        onClick={onStageClick}
      >
        <div className="hubv-focus">{tile(focused, 0, 80)}</div>
        {/* the strip's chevron (Discord's): it folds the strip away, for a
            bigger focus, and brings it back */}
        {others.length ? (
          <button
            type="button"
            className="hubv-strip-tog"
            aria-expanded={stripShown}
            aria-controls={stripShown ? stripId : undefined}
            aria-label={fold}
            data-tip={fold}
            data-strip-toggle=""
            onClick={() => setStripHidden(channelId, !stripHidden)}
          >
            <Icon name={stripShown ? 'chev-down' : 'chev-up'} size="s" />
          </button>
        ) : null}
        {stripShown ? (
          <div className="hubv-strip" id={stripId} role="list">
            {others.map((it, i) => tile(it, i + 1, 32, true))}
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <div className="hubv-vr-stage" data-layout="grid" ref={ref} onClick={onStageClick}>
      <div
        className="hubv-vr-grid"
        style={
          {
            '--cols': fit.cols,
            '--rows': fit.rows,
            '--oy': `${fit.oy}px`,
            ...size,
          } as CSSProperties
        }
      >
        {items.map((it, i) => tile(it, i, av, fit.w > 0 && fit.h < COMPACT_TILE_H))}
        {solo}
      </div>
    </div>
  );
}

/**
 * The push-to-talk status above the control bar (PTT mode only; the input
 * mode itself is the mic's caret menu and its "PTT" tag, P21). While you
 * hold the key: "Talking" only when you are heard; your own mute or deafen
 * says "You're muted" (gray, your own doing); a moderator's server or
 * channel mute or deafen, or the channel's Speak deny, says which (red, or
 * amber for the Speak deny), as your mic button does (review 2026-10-28:
 * it said "Talking" while comms-voice dropped every frame). Not holding it:
 * "Hold V to talk", and in a browser that it works only while the window is
 * focused. With no microphone: "listening only".
 */
export function PttChip() {
  const { t } = useT();
  const prefs = useVoice((v) => v.prefs);
  const tx = useVoice((v) => v.session.transmitting);
  const muted = useVoice((v) => v.session.selfMute || v.session.selfDeaf);
  const listenOnly = useVoice((v) => v.session.listenOnly);
  const own = useOwnServerFlags();
  const blocked = useVoice((v) => myVoiceBlocked(v.session));
  // the roster's `mute` without a server mute: Speak denied here (the dock's rule)
  const cantSpeak = useVoice((v) => myServerMute(v.session));
  const why: { block: SelfBlock; key: string } | null =
    ownBlockLine({ ...own, noTalk: own.noTalk || (cantSpeak && !own.serverMute) }) ??
    (blocked ? { block: 'no-talk', key: 'um.voice.flag.noTalk' } : null);
  const [focused, setFocused] = useState(() =>
    typeof document !== 'undefined' ? document.hasFocus() : true,
  );
  useEffect(() => {
    const on = () => setFocused(true);
    const off = () => setFocused(false);
    window.addEventListener('focus', on);
    window.addEventListener('blur', off);
    return () => {
      window.removeEventListener('focus', on);
      window.removeEventListener('blur', off);
    };
  }, []);
  if (listenOnly)
    return (
      <div className="hubv-ptt" data-state="no">
        <span>
          <Icon name="mic-off" size="s" />
          {t('ns.voice.listenOnly')}
        </span>
      </div>
    );
  // voice activation needs no line: the caret menu says the mode (L25, P21)
  if (prefs.inputMode !== 'ptt') return null;
  // a moderator's block first: unmuting yourself would not make you heard
  const st = tx ? (why ? 'blocked' : muted ? 'muted' : 'on') : 'idle';
  const web = pttMode() === 'focused';
  return (
    <div
      className="hubv-ptt"
      data-state={st}
      data-block={st === 'blocked' ? why?.block : undefined}
      aria-live="polite"
    >
      <span className="nx-swap" data-k={st}>
        {st === 'on' ? (
          <i className="hubv-ptt-dot" aria-hidden="true" />
        ) : st === 'muted' ? (
          <Icon name="mic-off" size="s" />
        ) : st === 'blocked' ? (
          <Icon
            size="s"
            name={
              why?.block === 'server'
                ? 'mic-off-mod'
                : why?.block === 'channel'
                  ? 'mic-off-ch'
                  : 'hand'
            }
          />
        ) : null}
        {st === 'on' ? (
          t('hubv.vr.pttTalking')
        ) : st === 'muted' ? (
          t('hubv.vr.pttMuted')
        ) : st === 'blocked' && why ? (
          t(why.key)
        ) : (
          <>
            {t('ns.voice.hold')} <kbd className="nx-kbd strong">{keyLabel(prefs.pttKey)}</kbd>{' '}
            {t('ns.voice.toTalk')}
          </>
        )}
      </span>
      {web ? (
        <span
          className={'ns-ptt-note' + (!focused ? ' warn' : '')}
          role={!focused ? 'status' : undefined}
        >
          {t(focused ? 'ns.voice.pttFocused' : 'ns.voice.pttBlurred')}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Push to talk by touch (§12.7 B2): a phone or tablet has no key to hold, so
 * while push to talk is the input mode the room shows a wide button above the
 * bar instead of the keyboard's line; holding it is holding the key
 * (`pttHold`). It says what the line would: "Hold to talk", "Talking" only
 * when you are heard, your own mute (gray), a moderator's block (red) or the
 * channel's Speak deny (amber). A lifted or cancelled finger, the window
 * losing focus or the room going away releases it, so no microphone stays
 * open. Space or Enter hold it from a keyboard. The long-press menu stays
 * off it (`data-no-longpress`).
 */
export function HoldToTalk() {
  const { t } = useT();
  const tx = useVoice((v) => v.session.transmitting);
  const muted = useVoice((v) => v.session.selfMute || v.session.selfDeaf);
  const own = useOwnServerFlags();
  const blocked = useVoice((v) => myVoiceBlocked(v.session));
  const cantSpeak = useVoice((v) => myServerMute(v.session));
  const why: { block: SelfBlock; key: string } | null =
    ownBlockLine({ ...own, noTalk: own.noTalk || (cantSpeak && !own.serverMute) }) ??
    (blocked ? { block: 'no-talk', key: 'um.voice.flag.noTalk' } : null);
  const held = useRef(false);
  const [down, setDown] = useState(false);
  const press = (on: boolean) => {
    if (held.current === on) return;
    held.current = on;
    setDown(on);
    pttHold(on);
  };
  useEffect(() => {
    const release = () => {
      if (!held.current) return;
      held.current = false;
      setDown(false);
      pttHold(false);
    };
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('blur', release);
      release();
    };
  }, []);
  const st = tx ? (why ? 'blocked' : muted ? 'muted' : 'on') : 'idle';
  const label =
    st === 'on'
      ? t('hubv.vr.pttTalking')
      : st === 'muted'
        ? t('hubv.vr.pttMuted')
        : st === 'blocked' && why
          ? t(why.key)
          : t('ns.voice.holdToTalk');
  const icon =
    st === 'muted'
      ? 'mic-off'
      : st === 'blocked'
        ? why?.block === 'server'
          ? 'mic-off-mod'
          : why?.block === 'channel'
            ? 'mic-off-ch'
            : 'hand'
        : 'mic';
  const isKey = (k: string) => k === ' ' || k === 'Enter';
  return (
    <button
      type="button"
      className="hubv-hold"
      data-state={st}
      data-block={st === 'blocked' ? why?.block : undefined}
      aria-pressed={down}
      aria-live="polite"
      data-no-longpress=""
      onPointerDown={(e) => {
        if (e.button > 0) return;
        // the finger may slide off the button: the hold stays until it lifts
        e.currentTarget.setPointerCapture?.(e.pointerId);
        press(true);
      }}
      onPointerUp={() => press(false)}
      onPointerCancel={() => press(false)}
      onLostPointerCapture={() => press(false)}
      onKeyDown={(e) => {
        if (!isKey(e.key)) return;
        e.preventDefault();
        if (!e.repeat) press(true);
      }}
      onKeyUp={(e) => {
        if (!isKey(e.key)) return;
        e.preventDefault();
        press(false);
      }}
      onBlur={() => press(false)}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Icon name={icon} />
      <span>{label}</span>
    </button>
  );
}

/**
 * The mic's caret menu (P21), also a right-click or long-press on mute:
 * Input device ›, the input mode and Input settings (hub/voiceQuick.ts).
 */
export { openInputMenu };

export function VoiceRoom({ channelId, chatOpen }: { channelId: Snowflake; chatOpen?: boolean }) {
  const { t, fmt } = useT();
  const channel = useData((d) => d.channels[channelId]);
  // the frame draws no ring: a speaking edge re-renders only the tiles' rings
  const s = useVoiceQuiet();
  const ptt = useVoice((v) => v.prefs.inputMode === 'ptt');
  const meId = useData((d) => d.me?.id);
  const people = useRoomPeople(channelId);
  // the header's view controls (Discord's): the people without video, full
  // screen for the whole room, and the chat's unread mark
  const roomRef = useRef<HTMLElement>(null);
  const roomFull = useFullscreen(roomRef);
  const video = useStageVideo(people, s, channelId, meId);
  const videoOnly = useStreamView((v) => !!v.videoOnly[channelId]);
  const unread = useDataShallow((d) => unreadOf(d, channelId));
  // "Open chat" on the channel (read its messages without joining) opens the chat here
  const [chat, setChat] = useState(!!chatOpen);
  const [askedChat, setAskedChat] = useState(chatOpen);
  if (chatOpen !== askedChat) {
    setAskedChat(chatOpen);
    if (chatOpen) setChat(true);
  }
  // the stream pop-out's "back": a phone's chat over the stage gives way
  const askN = useStreamView((x) => (x.ask?.channelId === channelId ? x.ask.n : 0));
  const [seenAsk, setSeenAsk] = useState(askN);
  if (askN !== seenAsk) {
    setSeenAsk(askN);
    if (askN && readLayout().layout === 'phone') setChat(false);
  }
  // phones and tablets (§12.7 B1): the chat is a pane (a drawer on a
  // tablet) over the room, and Back closes it first
  const lay = useLayout();
  const stacked = lay.layout !== 'desktop';
  useEffect(() => {
    if (!chat || !stacked) return;
    return pushBack(() => {
      setChat(false);
      if (chatOpen) goHub({ kind: 'voice', channelId });
    });
  }, [chat, stacked, chatOpen, channelId]);
  // a finger has no push-to-talk key: a hold button (§12.7 B2)
  const touch = lay.pointer === 'coarse';
  const own = useOwnServerFlags();
  const lift = useSelfLift();
  // your roles can't connect here (the sidebar's lock): no "Join" or "Join
  // again", which could only answer 403 (QA 2026-10-28: after "You no longer
  // have access to Lobby" it still offered "Join again"). A room you cannot
  // see is never one you may join on your own.
  const noConnect = useData((d) => {
    const c = d.channels[channelId];
    return !c || !can(channelPermissions(d, c), Permissions.CONNECT);
  });
  // a hidden room a moderator moved you into (QA D2): no channel reaches you,
  // but you are in it, so the room shows by the live roster under a neutral
  // name, without its chat (which you may not read)
  const hidden = !channel && s.channelId === channelId && s.status !== 'idle';
  if (!channel && !hidden) return null;
  const name = channel ? (channel.name ?? '') : t('ns.voice.privateRoom');
  const here = s.channelId === channelId || (s.targetId === channelId && s.status !== 'idle');
  const connected = here && s.status === 'connected';
  const failed = here && s.status === 'failed';
  // no microphone: the button says so and opens Voice & audio (HV-B08)
  const noMic = here && s.listenOnly && s.pid !== null;
  // a moderator's server or channel mute or deafen (red), or "can't speak
  // here" (an amber badge), on your own toggles, which keep showing your own
  // state, gray when off (live bug 2026-10-28; colours, owner 2026-10-28)
  const serverMuted = connected && own.serverMute;
  const serverDeafened = connected && own.serverDeaf;
  const channelMuted = connected && own.channelMute;
  const channelDeafened = connected && own.channelDeaf;
  const mic = micToggleView(
    {
      selfMute: s.selfMute,
      selfDeaf: s.selfDeaf,
      serverMute: serverMuted,
      // a server deafen silences you too (Discord): the mic says so
      serverDeaf: serverDeafened,
      channelMute: channelMuted,
      channelDeaf: channelDeafened,
      noTalk: connected && own.noTalk,
      lift,
    },
    { name: 'hubv.vr.mute', undo: 'hubv.vr.unmute' },
  );
  const sound = soundToggleView(
    {
      selfDeaf: s.selfDeaf,
      serverDeaf: serverDeafened,
      channelDeaf: channelDeafened,
      lift: lift.deaf,
    },
    { name: 'dock.deafen', undo: 'dock.undeafen' },
  );
  // the header's chips stay short (one line); that you can undo it
  // yourself is their tooltip. A chip that does not fit drops whole.
  const modKey = connected ? serverStatusKey(own) : null;
  const chKey = connected ? channelStatusKey(own) : null;
  const modSelfKey = connected ? serverStatusKey(own, lift) : null;
  const chSelfKey = connected ? channelStatusKey(own, lift) : null;
  const kbps = channel?.bitrate
    ? t('hubv.vr.kbps', { n: Math.round(channel.bitrate / 1000) })
    : undefined;
  const onStage = here && !failed;
  // the chat's unread mark while it is closed (Discord's): its mentions
  // in a badge, else a dot; muted rooms light no dot (unreadOf)
  const chatMark = chat ? null : unread.mentions ? 'mention' : unread.unread ? 'unread' : null;
  const chatLabel = t(chat ? 'hubv.vr.hideChat' : 'hubv.vr.chat');
  // you, alone in the room you are connected to
  const alone = connected && people.length === 1 && people[0]?.userId === meId;
  return (
    <section ref={roomRef} className="hub-pane nx-panel custom hubv-vroom" aria-label={name}>
      <header className="ch-head hubv-vr-head">
        <div className="ch-title">
          <span className="ch-title-i" aria-hidden="true">
            <Icon name="volume" size="m" />
          </span>
          {/* the bitrate is the name's tooltip (P21): no chip in the header */}
          <h1 dir="auto" data-tip={kbps} data-tip-pos="bottom">
            {name}
          </h1>
          {people.length ? (
            <>
              <span className="ch-sep" aria-hidden="true" />
              <span className="hubv-vr-count">{t('hubv.vr.inVoice', { n: people.length })}</span>
            </>
          ) : null}
          {channel ? <RoomStatus channelId={channelId} /> : null}
        </div>
        <div className="ch-acts">
          <span className="hubv-vr-meta">
            {modKey ? (
              <span
                className="ns-modchip"
                role="status"
                data-mod-status="server"
                data-tip={modSelfKey && modSelfKey !== modKey ? t(modSelfKey) : undefined}
                data-tip-pos="bottom"
              >
                <Icon name="shield" size="s" />
                <span>{t(modKey)}</span>
              </span>
            ) : null}
            {chKey ? (
              <span
                className="ns-modchip"
                role="status"
                data-mod-status="channel"
                data-tip={chSelfKey && chSelfKey !== chKey ? t(chSelfKey) : undefined}
                data-tip-pos="bottom"
              >
                <Icon name="volume" size="s" />
                <span>{t(chKey)}</span>
              </span>
            ) : null}
          </span>
          {/* Discord's "Show Non-Video Participants": shown while anything
              on the stage has video; remembered per room */}
          {onStage && video.any ? (
            <IconBtn
              icon="users"
              className={'hubv-vr-nonvideo' + (videoOnly ? ' on' : '')}
              label={t(videoOnly ? 'hubv.vr.showNonVideo' : 'hubv.vr.hideNonVideo')}
              tipPos="bottom"
              data-non-video={videoOnly ? 'hidden' : 'shown'}
              onClick={() => setVideoOnly(channelId, !videoOnly)}
            />
          ) : null}
          {/* the whole room full screen (the stage, the bar and the chat);
              not where only a video can go full screen (an iPhone) */}
          {onStage && fullscreenSupported() ? (
            <IconBtn
              icon="win-max"
              className="hubv-vr-fullbtn"
              label={t(roomFull ? 'stream.exitFullscreen' : 'stream.fullscreen')}
              tipPos="bottom"
              data-room-full={roomFull ? 'on' : 'off'}
              onClick={() => roomRef.current && toggleFullscreen(roomRef.current)}
            />
          ) : null}
          {hidden ? null : (
            <IconBtn
              icon="chats"
              className="hubv-vr-chatbtn"
              label={chatLabel}
              aria-label={
                chatLabel +
                (chatMark === 'mention'
                  ? ', ' + t('hub.ch.mentions', { n: unread.mentions })
                  : chatMark === 'unread'
                    ? ', ' + t('hub.ch.unread')
                    : '')
              }
              tipPos="bottom"
              pressed={chat}
              data-unread={chatMark ?? undefined}
              onClick={() => {
                setChat(!chat);
                // closed here: a later "Open chat" opens it again
                if (chat && chatOpen) goHub({ kind: 'voice', channelId });
              }}
            >
              {chatMark === 'mention' ? (
                <b className="nx-badge hubv-vr-chatbadge" aria-hidden="true">
                  {fmt.num(unread.mentions)}
                </b>
              ) : chatMark === 'unread' ? (
                <i className="hubv-vr-chatdot" aria-hidden="true" />
              ) : null}
            </IconBtn>
          )}
        </div>
      </header>
      <div
        className={'hubv-vr' + (chat && !hidden ? ' with-chat' : '')}
        data-state={here ? (connected ? 'connected' : 'connecting') : 'idle'}
      >
        <div className="hubv-vr-main">
          {here && !failed ? (
            <>
              <Stage
                people={people}
                s={s}
                meId={meId}
                guildId={channel?.guild_id ?? s.guildId}
                channelId={channelId}
                chatOver={chat && !hidden && lay.layout === 'phone'}
                solo={alone ? <SoloCard channelId={channelId} /> : undefined}
              />
              {!connected ? (
                <div className="hubv-vr-conn" aria-hidden="true">
                  <span>
                    <i className="nx-spin" />
                    {t(s.reconnecting ? 'ns.voice.reconnectingTo' : 'hubv.vr.connectingTo', {
                      name,
                    })}
                  </span>
                </div>
              ) : null}
              <div className="hubv-barwrap" data-kind="voice">
                {ptt && touch && !noMic ? <HoldToTalk /> : <PttChip />}
                <div
                  className="hubv-bar nx-glass strong"
                  role="toolbar"
                  aria-label={t('hubv.vr.controls')}
                >
                  <div className="hubv-split">
                    {/* a right-click or long-press on mute: the input menu (Discord's) */}
                    {noMic ? (
                      <button
                        type="button"
                        className="hubv-cb off"
                        aria-pressed={true}
                        aria-label={t('ns.voice.noMicTip')}
                        data-tip={t('ns.voice.noMicTip')}
                        data-no-mic
                        onClick={() => openSettings('voice')}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          openInputMenu(e.currentTarget.parentElement ?? e.currentTarget);
                        }}
                      >
                        <Icon name="mic-off" />
                      </button>
                    ) : (
                      <SelfToggleButton
                        view={mic}
                        cls="hubv-cb"
                        data={{
                          'data-server-muted': serverMuted || undefined,
                          'data-channel-muted': channelMuted || undefined,
                        }}
                        onClick={toggleMute}
                        onMenu={(el) => openInputMenu(el.parentElement ?? el)}
                      />
                    )}
                    {ptt && !noMic ? (
                      <span className="hubv-ptt-tag" aria-hidden="true">
                        {t('hubv.vr.pttTag')}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className="hubv-cbx"
                      aria-haspopup="menu"
                      aria-label={t('hubv.vr.inputOptions')}
                      data-tip={t('hubv.vr.inputOptions')}
                      onClick={(e) =>
                        openInputMenu(e.currentTarget.parentElement ?? e.currentTarget)
                      }
                    >
                      <Icon name="chev-up" size="s" />
                    </button>
                  </div>
                  <SelfToggleButton
                    view={sound}
                    cls="hubv-cb"
                    data={{
                      'data-server-deafened': serverDeafened || undefined,
                      'data-channel-deafened': channelDeafened || undefined,
                    }}
                    onClick={toggleDeaf}
                    onMenu={openOutputMenu}
                  />
                  {/* camera ("Coming soon" while video is off) and Go Live (§4.7) */}
                  {connected ? (
                    <>
                      <CameraButton cls="hubv-cb hubv-cb-cam" channelId={s.channelId} />
                      <ShareButton cls="hubv-cb hubv-cb-share" channelId={s.channelId} labeled />
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="hubv-cb hubv-cb-set"
                    aria-label={t('ns.set.voice')}
                    data-tip={t('ns.set.voice')}
                    onClick={() => openSettings('voice')}
                  >
                    <Icon name="gear" />
                  </button>
                  <span className="hubv-bar-sep" aria-hidden="true" />
                  <button
                    type="button"
                    className="hubv-cb leave"
                    aria-label={t('hubv.vr.leave')}
                    data-tip={t('hubv.vr.leave')}
                    onClick={() => leaveVoice()}
                  >
                    <Icon name="phone-off" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="hubv-vr-idle nx-arrive">
              <span className="hubv-vr-idle-ic">
                <Icon name={failed ? 'alert' : 'volume'} size="xl" />
              </span>
              <h2>{failed ? t('ns.voice.lostIn', { name }) : t('hubv.vr.notIn', { name })}</h2>
              <p>
                {failed && s.error
                  ? // the reason, worded for "Couldn't join X: …" and the dock's
                    // "Room · …", stands alone here: a capital first letter
                    asSentence(t(`ns.voice.err.${s.error}`, { channel: name }))
                  : people.length
                    ? t('hubv.vr.inside', { n: people.length })
                    : t('hubv.vr.empty')}
              </p>
              {people.length ? (
                <div className="hubv-stack">
                  {people.map((p, i) => (
                    <span key={p.userId} className="vc-stk" style={{ '--i': i } as CSSProperties}>
                      <StackAvatar userId={p.userId} />
                    </span>
                  ))}
                </div>
              ) : null}
              {/* who is live in there: Join and watch (§1), never a preview */}
              {!noConnect && !failed
                ? people.map((p) => (
                    <LiveChip
                      key={p.userId}
                      userId={p.userId}
                      channelId={channelId}
                      guildId={channel?.guild_id ?? null}
                    />
                  ))
                : null}
              {noConnect ? (
                failed && s.error === 'lost_access' ? null : (
                  <p className="ns-muted" data-no-connect="">
                    {t('hub.vc.noConnect')}
                  </p>
                )
              ) : (
                <Btn kind="mode" icon="volume" onClick={() => void joinVoiceFromUi(channelId)}>
                  {failed ? t('ns.voice.rejoin') : t('hubv.vr.join')}
                </Btn>
              )}
            </div>
          )}
        </div>
        {chat && !hidden ? (
          <aside className="hubv-vr-chat">
            <ChannelView channelId={channelId} embedded />
          </aside>
        ) : null}
      </div>
    </section>
  );
}

/** How long the room's invite lasts (Discord's default: 7 days). */
const SOLO_INVITE_AGE_S = 7 * 24 * 3600;

/**
 * "It's just you here" (Discord's empty call): a glass card a tile's size
 * after your own tile while nobody else is in the room you are connected
 * to. Where you may create invites, Copy invite makes one to this room
 * (one of yours that is still good comes back) and copies its link.
 */
function SoloCard({ channelId }: { channelId: Snowflake }) {
  const { t } = useT();
  const canInvite = useData((d) => {
    const c = d.channels[channelId];
    return !!c?.guild_id && can(channelPermissions(d, c), Permissions.CREATE_INSTANT_INVITE);
  });
  const [busy, setBusy] = useState(false);
  const copyInvite = async () => {
    setBusy(true);
    const r = await runAction(() =>
      api.um.createInvite(channelId, { max_age: SOLO_INVITE_AGE_S, max_uses: 0 }),
    );
    setBusy(false);
    if (!r.ok) return;
    const link = `${serverOrigin()}/invite/${encodeURIComponent(r.value.code)}`;
    if (await copyText(link)) toast(t('hubv.vr.inviteCopied'), { icon: 'link', tone: 'ok' });
    // refused: say so, with the link to copy by hand
    else
      toast(t('ns.copy.failed', { text: link }), { icon: 'alert', tone: 'warn', duration: 9000 });
  };
  return (
    <div className="hubv-solo nx-arrive" role="group" aria-label={t('hubv.vr.solo')}>
      <span className="hubv-solo-ic" aria-hidden="true">
        <Icon name="user-plus" />
      </span>
      <b>{t('hubv.vr.solo')}</b>
      <span className="hubv-solo-t">
        {t(canInvite ? 'hubv.vr.soloInvite' : 'hubv.vr.soloWait')}
      </span>
      {canInvite ? (
        <Btn
          kind="secondary"
          size="sm"
          icon="link"
          busy={busy}
          disabled={busy}
          data-copy-invite=""
          onClick={() => void copyInvite()}
        >
          {t('hubv.vr.copyInvite')}
        </Btn>
      ) : null}
    </div>
  );
}

function StackAvatar({ userId }: { userId: Snowflake }) {
  const user = useData((d) => d.users[userId]);
  return <Avatar user={user} size={40} />;
}

/** "Bahar is live · Join and watch" in a room you are not in (glass, one per live person). */
function LiveChip({
  userId,
  channelId,
  guildId,
}: {
  userId: Snowflake;
  channelId: Snowflake;
  guildId: Snowflake | null;
}) {
  const { t } = useT();
  const media = useMediaOf(userId, guildId);
  const user = useData((d) => d.users[userId]);
  const name = useData((d) =>
    user ? displayName(user, guildId ? d.members[guildId]?.[userId] : undefined) : '',
  );
  if (!media.live) return null;
  return (
    <button
      type="button"
      className="hubv-livechip"
      data-live={userId}
      onClick={() => void joinAndWatch(channelId, userId)}
    >
      <span className="nx-live-badge">{t('stream.live')}</span>
      <span className="hubv-livechip-t">
        <bdi>{name}</bdi>
      </span>
      <span className="hubv-livechip-go">
        <Icon name="eye" size="s" />
        {t('stream.watchJoin')}
      </span>
    </button>
  );
}
